import type { ActionHistoryEntry } from "@credit-acceptance/step6-bff-client";
import { useQuery } from "@tanstack/react-query";

import { workbenchStipulationsApi } from "../../../api/client";
import { stipulationKeys } from "./stipulationKeys";

export interface StipulationHistoryEntry {
  actionCode?: string;
  previousAction?: string;
  reasonDescription?: string;
  noteText?: string;
  performedBy?: string;
  performedAt?: string;
}

/**
 *
 * @param entry
 */
function mapEntry(entry: ActionHistoryEntry): StipulationHistoryEntry {
  return {
    actionCode: entry.actionCode,
    previousAction: entry.previousAction,
    reasonDescription: entry.reasonDescription,
    noteText: entry.noteText,
    performedBy: entry.performedBy,
    performedAt: entry.performedAt,
  };
}

export interface UseStipulationHistoryOptions {
  approvalId: string;
  stipCode: string;
  applicantPk?: number;
  enabled: boolean;
}

/**
 * Fetches the action history for a single stipulation from the BFF.
 * Only runs when `enabled` is true (i.e. the row is expanded). With the
 * default staleTime of 0, data is always considered stale so each expand
 * triggers a fresh fetch.
 * @param root0
 * @param root0.approvalId
 * @param root0.stipCode
 * @param root0.applicantPk
 * @param root0.enabled
 */
export function useStipulationHistory({ approvalId, stipCode, applicantPk, enabled }: UseStipulationHistoryOptions) {
  return useQuery({
    queryKey: stipulationKeys.history({ approvalId, stipCode, applicantPk }),
    queryFn: async ({ signal }): Promise<StipulationHistoryEntry[]> => {
      const response = await workbenchStipulationsApi.getStipulationHistory(
        approvalId,
        stipCode,
        // applicantPk is a query param — the BFF treats it as optional at the
        // HTTP level even though the generated client types it as required.
        applicantPk as number,
        { signal }
      );
      return (response.data.actions ?? []).map((entry: ActionHistoryEntry) => mapEntry(entry));
    },
    enabled: enabled && !!approvalId && !!stipCode,
    staleTime: 0,
  });
}
